import type { components } from "@/api/schema.d.ts";
import router from "@/router";
import { useAppStateStore } from "@/stores/AppStateStore";
import { useCampaignEffectivenessStore } from "@/stores/CampaignEffectivenessStore";
import { useMetaAudienceStore } from "@/stores/MetaAudienceStore";
import { useResearchStudioStore } from "@/stores/ResearchStudioStore";
import { useUserActivityStore } from "@/stores/UserActivityStore";
import { useUserStore } from "@/stores/UserStore";

type UserProfile = components["schemas"]["UserProfile"];

const orgCollidesWithAppClient = (
  user: UserProfile,
  org: { name: string },
): boolean => user.app_clients?.some((c) => c.name === org.name) ?? false;

/** Exported for unit tests — default app-client selection for impersonation. */
export const getDefaultAppClientId = (
  user: UserProfile,
): number | undefined => {
  if (!user.app_clients?.length) return;

  if (user.default_app_client_id) return user.default_app_client_id;

  // Prefer an app client over a same-named default org (post-migration).
  if (user.default_org_id) {
    const defaultOrg = user.orgs.find((o) => o.id === user.default_org_id);
    const colliding = defaultOrg
      ? user.app_clients.find((c) => c.name === defaultOrg.name)
      : undefined;
    if (colliding) return colliding.id;
  }

  // No default org: if the alphabetical org fallback collides, prefer that app client.
  if (!user.default_org_id && user.orgs.length) {
    const alphaOrg = [...user.orgs].sort((a, b) =>
      a.name.localeCompare(b.name),
    )[0];
    return user.app_clients.find((c) => c.name === alphaOrg.name)?.id;
  }

  if (user.orgs.length) return;
  return [...user.app_clients].sort((a, b) => a.name.localeCompare(b.name))[0]
    .id;
};

/** Exported for unit tests — default org selection for impersonation. */
export const getDefaultOrgId = (user: UserProfile): number | undefined => {
  if (!user.orgs.length) return;

  if (user.default_org_id) {
    const org = user.orgs.find((o) => o.id === user.default_org_id);
    // Skip orgs that collide with an app client name — caller prefers app client.
    if (org && orgCollidesWithAppClient(user, org)) return;
    return user.default_org_id;
  }

  // No default: exclude colliding orgs, then pick alphabetically.
  const nonColliding = [...user.orgs]
    .filter((o) => !orgCollidesWithAppClient(user, o))
    .sort((a, b) => a.name.localeCompare(b.name));
  return nonColliding[0]?.id;
};

export const useImpersonation = () => {
  const userStore = useUserStore();
  const appStateStore = useAppStateStore();
  const userActivityStore = useUserActivityStore();
  const metaAudienceStore = useMetaAudienceStore();
  const campaignEffectivenessStore = useCampaignEffectivenessStore();
  const researchStudioStore = useResearchStudioStore();

  const impersonateUser = (
    user: UserProfile,
    org_id?: number,
    app_client_id?: number,
  ) => {
    let requireMetaAudiences = false;
    const impersonator = { ...userStore.currentUser };
    userStore.loadingImpersonatedUser = user.id;
    userStore.setImpersonating(user.id);
    appStateStore.breadcrumbs = [];

    userStore.setCurrentOrg(undefined);
    userStore.setCurrentAppClient(undefined);

    if (app_client_id) {
      userStore.setCurrentAppClient(app_client_id);
    } else if (org_id) {
      userStore.setCurrentOrg(org_id);
      requireMetaAudiences = true;
    } else {
      const defaultAppClientId = getDefaultAppClientId(user);
      if (defaultAppClientId) {
        userStore.setCurrentAppClient(defaultAppClientId);
      } else {
        userStore.setCurrentOrg(getDefaultOrgId(user));
        requireMetaAudiences = true;
      }
    }

    const requests = [
      userActivityStore.create({
        event: "impersonated login",
        meta_data: { impersonator: impersonator?.email },
      }),
      campaignEffectivenessStore.listCampaigns(),
      researchStudioStore.listSurveys(),
    ];

    if (requireMetaAudiences) {
      requests.push(metaAudienceStore.getMetaAudiences());
    }

    Promise.all(requests)
      .then(() => {
        router.push({ name: "home" }).catch(() => {});
      })
      .finally(() => {
        userStore.loadingImpersonatedUser = undefined;
      });
  };

  return { impersonateUser };
};
